import { Link, useLocation } from "@tanstack/react-router";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "~/components/ui/accordion";
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  CardFooter,
} from "~/components/ui/card";

interface FaqItem {
  id: string;
  question: string;
  answer: string;
}

export function FaqCard() {
  const location = useLocation();

  // Language detection (same pattern as AboutCard/ProjectsCard)
  const getLanguage = () => {
    const segments = location.pathname.split("/").filter(Boolean);
    const langs = ["uz", "ru", "en"];
    return langs.includes(segments[0]) ? segments[0] : "oz";
  };

  // Helper to get language-aware page path
  const getPagePath = (page: string) => {
    const lang = getLanguage();
    return lang === "oz" ? `/${page}` : `/${lang}/${page}`;
  };

  // Multi-language content with top 5 FAQs
  const content = {
    "uz-Latn": {
      title: "Ko'p so'raladigan savollar",
      faqs: [
        {
          id: "faq-what-is",
          question: "Lotin.uz o'zi nima?",
          answer:
            "Lotin.uz sayti orqali siz so'zlarni o'zbek kirill yozuvidan o'zbek lotin yozuviga o'tkazishingiz mumkin.",
        },
        {
          id: "faq-difference",
          question:
            "Boshqa lotin-kirill, kirill-lotin tarjimonlaridan nima farqi bor?",
          answer:
            "Lotin.uz boshqa o'xshash saytlardan farqli o'laroq xato o'girmaydigan so'zlarni aynan nusxa ko'chiradi. Kirill yozuvida kirillcha emas balki lotincha yozilgan shaxs nomlari, geografik nomlar va brendlar nomlarini o'girish shart emas. Bunday nomlarning lotin va kirill yozuvlarda imlosida farq yo'q. Lotin.uz ularni aynan nusxa ko'chiradi, o'girmaydi.",
        },
        {
          id: "faq-availability",
          question: "Lotinuz saytidan qachon foydalansam bo'ladi?",
          answer:
            "Lotin.uz xizmatidan istalgan vaqt, istalgan joydan foydalanishingiz mumkin.",
        },
        {
          id: "faq-desktop-app",
          question:
            "Microsoft Word yoki Excel uchun translit programmasi bormi?",
          answer:
            "Microsoft Word uchun yoki desktop dastur uchun translit programmamiz yo'q, ammo ayni vaqtda buni amalga oshirishni o'ylayapmiz.",
        },
        {
          id: "faq-support",
          question:
            "Saytingiz judayam yoqdi. Sizga qanday qilib yordam bera olaman?",
          answer:
            "Rahmat! Bizning jamoamiz sizning ma'qullashingizdan xursandmiz. Agar loyihamizni qo'llab-quvvatlash istagi tug'ilgan bo'lsa ushbu karta raqamiga ixtiyoriy miqdorda pul o'tkazishingiz mumkin: 5614 6825 1643 3986",
        },
      ],
      seeMore: "Batafsil →",
    },
    "uz-Cyrl": {
      title: "Кўп сўраладиган саволлар",
      faqs: [
        {
          id: "faq-what-is",
          question: "Lotin.uz ўзи нима?",
          answer:
            "Lotin.uz сайти орқали сиз сўзларни ўзбек кирилл ёзувидан ўзбек лотин ёзувига ўтказишингиз мумкин.",
        },
        {
          id: "faq-difference",
          question:
            "Бошқа лотин-кирилл, кирилл-лотин тарғимонларидан нима фарқи бор?",
          answer:
            "Lotin.uz бошқа ўхшаш сайтлардан фарқли ўлароқ хато ўгирмайдиган сўзларни айнан нусха кўчиради. Кирилл ёзувида кириллча эмас балки лотинча ёзилган шахс номлари, географик номлар ва брендлар номларини ўгириш шарт эмас. Бундай номларнинг лотин ва кирилл ёзувларда имлосида фарқ йўқ. Lotin.uz уларни айнан нусха кўчиради, ўгирмайди.",
        },
        {
          id: "faq-availability",
          question: "Lotinuz сайтидан қачон фойдалансам бўлади?",
          answer:
            "Lotin.uz хизматидан исталган вақт, исталган жойдан фойдаланишингиз мумкин.",
        },
        {
          id: "faq-desktop-app",
          question: "Microsoft Word ёки Excel учун транслит программаси борми?",
          answer:
            "Microsoft Word учун ёки desktop дастур учун транслит программамиз йўқ, аммо айни вақтда буни амалга оширишни ўйлаяпмиз.",
        },
        {
          id: "faq-support",
          question:
            "Сайтингиз жудаям ёқди. Сизга қандай қилиб ёрдам бера оламан?",
          answer:
            "Раҳмат! Бизнинг жамоамиз сизнинг маъқуллашингиздан хурсандмиз. Агар лойиҳамизни қўллаб-қувватлаш истаги туғилган бўлса ушбу карта рақамига ихтиёрий миқдорда пул ўтказишингиз мумкин: 5614 6825 1643 3986",
        },
      ],
      seeMore: "Батафсил →",
    },
    ru: {
      title: "Часто задаваемые вопросы",
      faqs: [
        {
          id: "faq-what-is",
          question: "Что такое Lotin.uz?",
          answer:
            "Через сайт Lotin.uz вы можете конвертировать слова из узбекской кириллицы в узбекскую латиницу.",
        },
        {
          id: "faq-difference",
          question:
            "Чем отличается от других латин-кирилл, кирилл-латин переводчиков?",
          answer:
            "В отличие от других подобных сайтов, Lotin.uz не переводит слова, которые не должны переводиться. Имена людей, географические названия и названия брендов, написанные латиницей в кириллическом тексте, не нужно переводить. В написании таких названий нет разницы между латиницей и кириллицей. Lotin.uz копирует их точно, не переводя.",
        },
        {
          id: "faq-availability",
          question: "Когда можно пользоваться сайтом Lotinuz?",
          answer:
            "Вы можете пользоваться услугами Lotin.uz в любое время, из любого места.",
        },
        {
          id: "faq-desktop-app",
          question: "Есть ли программа транслита для Microsoft Word или Excel?",
          answer:
            "У нас нет программы транслита для Microsoft Word или настольного приложения, но в настоящее время мы рассматриваем возможность ее создания.",
        },
        {
          id: "faq-support",
          question: "Очень понравился ваш сайт. Как я могу вам помочь?",
          answer:
            "Спасибо! Наша команда рада вашему одобрению. Если у вас появилось желание поддержать наш проект, вы можете перевести любую сумму на этот номер карты: 5614 6825 1643 3986",
        },
      ],
      seeMore: "Подробнее →",
    },
    "en-US": {
      title: "Frequently Asked Questions",
      faqs: [
        {
          id: "faq-what-is",
          question: "What is Lotin.uz?",
          answer:
            "Through Lotin.uz website, you can convert words from Uzbek Cyrillic to Uzbek Latin script.",
        },
        {
          id: "faq-difference",
          question:
            "What's the difference from other Latin-Cyrillic converters?",
          answer:
            "Unlike other similar sites, Lotin.uz doesn't translate words that shouldn't be translated. Personal names, geographic names, and brand names written in Latin within Cyrillic text don't need to be translated. There's no difference in spelling of such names between Latin and Cyrillic. Lotin.uz copies them exactly, without translating.",
        },
        {
          id: "faq-availability",
          question: "When can I use Lotinuz website?",
          answer: "You can use Lotin.uz services anytime, from anywhere.",
        },
        {
          id: "faq-desktop-app",
          question: "Is there a translit program for Microsoft Word or Excel?",
          answer:
            "We don't have a translit program for Microsoft Word or a desktop application, but we're currently considering creating one.",
        },
        {
          id: "faq-support",
          question: "I really like your site. How can I help you?",
          answer:
            "Thank you! Our team is pleased with your approval. If you wish to support our project, you can transfer any amount to this card number: 5614 6825 1643 3986",
        },
      ],
      seeMore: "Learn more →",
    },
  };

  const langMap: Record<string, keyof typeof content> = {
    oz: "uz-Latn",
    uz: "uz-Cyrl",
    ru: "ru",
    en: "en-US",
  };

  const currentLang = getLanguage();
  const currentContent = content[langMap[currentLang]] || content["uz-Latn"];

  return (
    <Card className="shrink-0">
      <CardHeader>
        <CardTitle>{currentContent.title}</CardTitle>
      </CardHeader>
      <CardContent>
        <Accordion type="single" collapsible>
          {currentContent.faqs.map((faq) => (
            <AccordionItem key={faq.id} value={faq.id}>
              <AccordionTrigger>
                <span>{faq.question}</span>
              </AccordionTrigger>
              <AccordionContent>
                <div>{faq.answer}</div>
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </CardContent>
      <CardFooter>
        <Link to={getPagePath("faq")} className="font-medium">
          {currentContent.seeMore}
        </Link>
      </CardFooter>
    </Card>
  );
}
